import { Component, computed, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NbTabComponent } from '@nebular/theme';
import { EquipmentService } from '@artemis-software/wr-api';
import { TgaTypeFormComponent } from './tga-type-form/tga-type-form.component';
import { TgaTypeCollectionsComponent } from './tga-type-collections/tga-type-collections.component';
import { TgaTypeProtocolTypesComponent } from './tga-type-protocol-types/tga-type-protocol-types.component';
import {
  TgaTypeMaintenanceTimerConfigsComponent
} from './tga-type-maintenance-timer-configs/tga-type-maintenance-timer-configs.component';

@Component({
  standalone: false,
  selector: 'wr-tga-type-detail-page',
  templateUrl: './tga-type-detail-page.component.html',
  styleUrls: ['./tga-type-detail-page.component.scss'],
})
export class TgaTypeDetailPageComponent implements OnDestroy {

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly equipmentService = inject(EquipmentService);

  private readonly destroy$ = new Subject<void>();

  readonly id = signal<string | null>(null);
  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.id() === 'new');
  readonly isEditing = signal(false);

  private activeTabTitle = 'Stammdaten';

  @ViewChild(TgaTypeFormComponent) formComponent?: TgaTypeFormComponent;
  @ViewChild(TgaTypeCollectionsComponent) collectionsComponent?: TgaTypeCollectionsComponent;
  @ViewChild(TgaTypeProtocolTypesComponent) protocolTypesComponent?: TgaTypeProtocolTypesComponent;
  @ViewChild(TgaTypeMaintenanceTimerConfigsComponent) maintenanceTimerConfigsComponent?: TgaTypeMaintenanceTimerConfigsComponent;

  constructor() {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      this.id.set(id);
      if (id === 'new') {
        this.canEdit.set(true);
        this.isEditing.set(true);
      } else if (id) {
        this.equipmentService.findById(id).subscribe(dto => {
          this.canEdit.set(dto.permissions.canEdit);
        });
      }
    });
  }

  onTabChange(tab: NbTabComponent): void {
    this.activeTabTitle = tab.tabTitle ?? 'Stammdaten';
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.formComponent?.onEdit();
    this.collectionsComponent?.onEdit();
    this.protocolTypesComponent?.onEdit();
    this.maintenanceTimerConfigsComponent?.onEdit();
  }

  onDelete(): void {
    this.formComponent?.onDelete();
  }

  async submit(): Promise<void> {
    switch (this.activeTabTitle) {
      case 'Stammdaten': await this.formComponent?.submit(); break;
      case 'Feldgruppen': await this.collectionsComponent?.submit(); break;
      case 'Protokolltypen': await this.protocolTypesComponent?.submit(); break;
      case 'Wartungstimer': await this.maintenanceTimerConfigsComponent?.submit(); break;
    }
    this.isEditing.set(false);
  }

  async cancel(): Promise<void> {
    await this.formComponent?.cancel();
    await this.collectionsComponent?.cancel();
    await this.protocolTypesComponent?.cancel();
    await this.maintenanceTimerConfigsComponent?.cancel();
    this.isEditing.set(false);
  }

  navigateBack(): void {
    this.router.navigate(['equipments']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
